import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { WalkieAudioEngine } from '../lib/walkieAudioEngine';

// ─── Types ───

export interface Channel {
  id: string;
  name: string;
  type: 'site' | 'sector' | 'department' | 'emergency' | 'custom';
  memberCount: number;
  activeSpeaker?: string | null;
}

export interface ChannelMember {
  userId: string;
  name: string;
  role: string;
  isMuted: boolean;
  isSpeaking: boolean;
}

export interface Transmission {
  id: string;
  userId: string;
  userName: string;
  channelId: string;
  channelName: string;
  duration: number; // seconds
  timestamp: string; // ISO
}

export interface SOSAlert {
  id: string;
  guardName: string;
  guardId: string;
  channelId: string;
  channelName: string;
  lat: number;
  lng: number;
  timestamp: string;
  resolved: boolean;
}

interface WalkieState {
  connected: boolean;
  connecting: boolean;
  channels: Channel[];
  activeChannelId: string | null;
  members: ChannelMember[];
  transmissions: Transmission[];
  sosAlerts: SOSAlert[];
  isTransmitting: boolean;
  isPttLocked: boolean;
  error: string | null;
  audioEngine: WalkieAudioEngine | null;
  userRole: string;
  token: string;
}

interface WalkieContextValue extends WalkieState {
  pttStart: () => void;
  pttStop: () => void;
  switchChannel: (channelId: string) => void;
  toggleMemberMute: (userId: string) => void;
  resolveSOS: (alertId: string) => void;
  login: (employeeId: string, password: string) => void;
  logout: () => void;
  userRole: string;
  token: string;
  createChannel: (name: string, type: string, maxUsers: number) => Promise<void>;
  deleteChannel: (channelId: string) => Promise<void>;
  addMemberToChannel: (channelId: string, employeeId: string) => Promise<void>;
  removeMemberFromChannel: (channelId: string, userId: string) => Promise<void>;
  refreshChannels: () => void;
}

// ─── Constants ───

const BACKEND_URL = 'https://secure.hemaya.cloud';
const WS_PATH = '/socket.io/';
const API_BASE = 'https://secure.hemaya.cloud/api/walkie';

// ─── Context ───

const WalkieContext = createContext<WalkieContextValue | null>(null);

export function useWalkie(): WalkieContextValue {
  const ctx = useContext(WalkieContext);
  if (!ctx) throw new Error('useWalkie must be used within WalkieProvider');
  return ctx;
}

export function WalkieProvider({ children }: { children: ReactNode }) {
  const socketRef = useRef<Socket | null>(null);
  const audioEngineRef = useRef<WalkieAudioEngine | null>(null);
  const pttActiveRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const pttIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [state, setState] = useState<WalkieState>({
    connected: false,
    connecting: false,
    channels: [],
    activeChannelId: null,
    members: [],
    transmissions: [],
    sosAlerts: [],
    isTransmitting: false,
    isPttLocked: false,
    error: null,
    audioEngine: null,
    userRole: '',
    token: '',
  });

  // ─── Audio Engine Setup ───
  const ensureAudioEngine = useCallback(() => {
    if (!audioEngineRef.current) {
      audioEngineRef.current = new WalkieAudioEngine(() => {
        setState(s => ({ ...s })); // trigger re-render on audio state change
      });
    }
    return audioEngineRef.current;
  }, []);

  // ─── Socket Connection ───
  const connect = useCallback((token: string) => {
    if (socketRef.current?.connected) return;

    setState(s => ({ ...s, connecting: true, error: null }));

    const socket = io(BACKEND_URL, {
      path: WS_PATH,
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;

    // ── Connection events ──
    socket.on('connect', () => {
      setState(s => ({ ...s, connected: true, connecting: false, error: null }));
      socket.emit('ops:join'); // join ops room (monitors all channels)
    });

    socket.on('disconnect', (reason) => {
      setState(s => ({ ...s, connected: false, connecting: false, error: `انقطع الاتصال: ${reason}` }));
    });

    socket.on('connect_error', (err) => {
      setState(s => ({ ...s, connected: false, connecting: false, error: `خطأ الاتصال: ${err.message}` }));
    });

    // ── Channel state ──
    socket.on('channels:list', (channels: Channel[]) => {
      setState(s => ({
        ...s,
        channels,
        activeChannelId: s.activeChannelId ?? channels[0]?.id ?? null,
      }));
    });

    socket.on('channel:updated', (channel: Channel) => {
      setState(s => ({
        ...s,
        channels: s.channels.map(c => c.id === channel.id ? channel : c),
      }));
    });

    // ── Members ──
    socket.on('members:list', (members: ChannelMember[]) => {
      setState(s => ({ ...s, members }));
    });

    socket.on('member:joined', (member: ChannelMember) => {
      setState(s => ({
        ...s,
        members: [...s.members.filter(m => m.userId !== member.userId), member],
      }));
    });

    socket.on('member:left', (data: { userId: string }) => {
      setState(s => ({
        ...s,
        members: s.members.filter(m => m.userId !== data.userId),
      }));
    });

    // ── Audio: incoming PCM ──
    socket.on('audio:pcm', (data: { channelId: string; userId: string; userName: string; pcm: ArrayBuffer }) => {
      const engine = ensureAudioEngine();
      engine.init().then(() => {
        engine.enqueuePCM(data.pcm);
      });

      // Update active speaker
      setState(s => ({
        ...s,
        channels: s.channels.map(c =>
          c.id === data.channelId ? { ...c, activeSpeaker: data.userName } : c
        ),
      }));
    });

    socket.on('audio:end', (data: { channelId: string; userId: string; duration: number }) => {
      // Add to transmission history
      setState(s => {
        const channel = s.channels.find(c => c.id === data.channelId);
        const member = s.members.find(m => m.userId === data.userId);
        const newTrans: Transmission = {
          id: `tr-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          userId: data.userId,
          userName: member?.name ?? 'غير معروف',
          channelId: data.channelId,
          channelName: channel?.name ?? '',
          duration: data.duration,
          timestamp: new Date().toISOString(),
        };
        return {
          ...s,
          transmissions: [newTrans, ...s.transmissions].slice(0, 100),
          channels: s.channels.map(c =>
            c.id === data.channelId ? { ...c, activeSpeaker: null } : c
          ),
        };
      });
    });

    // ── PTT lock (another user is talking) ──
    socket.on('ptt:locked', (data: { userId: string; userName: string; channelId: string }) => {
      setState(s => ({ ...s, isPttLocked: true }));
    });

    socket.on('ptt:unlocked', () => {
      setState(s => ({ ...s, isPttLocked: false }));
    });

    // ── SOS ──
    socket.on('sos:alert', (alert: SOSAlert) => {
      setState(s => ({ ...s, sosAlerts: [alert, ...s.sosAlerts.filter(a => a.id !== alert.id)] }));
    });

    socket.on('sos:resolved', (data: { alertId: string }) => {
      setState(s => ({
        ...s,
        sosAlerts: s.sosAlerts.filter(a => a.id !== data.alertId),
      }));
    });

    // ── Error ──
    socket.on('error', (err: { message: string }) => {
      setState(s => ({ ...s, error: err.message }));
    });
  }, [ensureAudioEngine]);

  // ─── PTT Start ───
  const pttStart = useCallback(() => {
    if (pttActiveRef.current) return;
    if (state.isPttLocked) return;
    if (!state.activeChannelId) return;
    if (!socketRef.current?.connected) return;

    pttActiveRef.current = true;

    // Request mic and start capturing PCM
    navigator.mediaDevices.getUserMedia({ audio: {
      sampleRate: 8000,
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
    }}).then(stream => {
      audioStreamRef.current = stream;

      // Use AudioContext to downsample to 8000Hz PCM 16-bit
      const audioCtx = new AudioContext({ sampleRate: 8000 });
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (e) => {
        if (!pttActiveRef.current) return;
        const inputData = e.inputBuffer.getChannelData(0);
        // Convert float32 to 16-bit PCM
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        // Send as binary
        socketRef.current?.emit('audio:pcm', {
          channelId: state.activeChannelId,
          pcm: pcm16.buffer,
        });
      };

      source.connect(processor);
      processor.connect(audioCtx.destination);

      mediaRecorderRef.current = { processor, audioCtx } as any;

      socketRef.current.emit('ptt:start', { channelId: state.activeChannelId });
      setState(s => ({ ...s, isTransmitting: true }));
    }).catch(err => {
      setState(s => ({ ...s, error: `لا يمكن الوصول للميكروفون: ${err.message}` }));
      pttActiveRef.current = false;
    });
  }, [state.activeChannelId, state.isPttLocked]);

  // ─── PTT Stop ───
  const pttStop = useCallback(() => {
    if (!pttActiveRef.current) return;
    pttActiveRef.current = false;

    // Stop mic
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }

    // Cleanup processor
    const recorder = mediaRecorderRef.current as any;
    if (recorder?.processor) {
      recorder.processor.disconnect();
      recorder.processor = null;
    }
    if (recorder?.audioCtx) {
      recorder.audioCtx.close();
    }
    mediaRecorderRef.current = null;

    if (socketRef.current?.connected && state.activeChannelId) {
      socketRef.current.emit('ptt:stop', { channelId: state.activeChannelId });
    }

    setState(s => ({ ...s, isTransmitting: false }));
  }, [state.activeChannelId]);

  // ─── Switch Channel ───
  const switchChannel = useCallback((channelId: string) => {
    setState(s => ({ ...s, activeChannelId: channelId }));
    socketRef.current?.emit('channel:switch', { channelId });
  }, []);

  // ─── Toggle Member Mute (local only) ───
  const toggleMemberMute = useCallback((userId: string) => {
    setState(s => ({
      ...s,
      members: s.members.map(m =>
        m.userId === userId ? { ...m, isMuted: !m.isMuted } : m
      ),
    }));
  }, []);

  // ─── Resolve SOS ───
  const resolveSOS = useCallback((alertId: string) => {
    socketRef.current?.emit('sos:resolve', { alertId });
  }, []);

  // ─── Login / Logout ───
  const login = useCallback(async (employeeId: string, password: string) => {
    try {
      const res = await fetch(`${API_BASE.replace('/walkie', '')}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.token) {
        setState(s => ({ ...s, error: data.error || data.message || 'فشل تسجيل الدخول' }));
        return;
      }
      const token = data.token;
      const role = data.user?.role || data.role || 'guard';
      localStorage.setItem('hemaya_token', token);
      localStorage.setItem('hemaya_role', role);
      setState(s => ({ ...s, token, userRole: role }));
      // Also fetch initial channels via REST
      fetch(`${API_BASE}/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(channels => {
          if (Array.isArray(channels)) {
            setState(s => ({ ...s, channels }));
          } else {
            setState(s => ({ ...s, channels: [] }));
          }
        })
        .catch(() => {});
      connect(token);
    } catch (e) {
      setState(s => ({ ...s, error: 'خطأ في الاتصال بالخادم' }));
    }
  }, [connect]);

  const logout = useCallback(() => {
    localStorage.removeItem('hemaya_token');
    localStorage.removeItem('hemaya_role');
    socketRef.current?.disconnect();
    socketRef.current = null;
    audioEngineRef.current?.destroy();
    audioEngineRef.current = null;
    setState({
      connected: false,
      connecting: false,
      channels: [],
      activeChannelId: null,
      members: [],
      transmissions: [],
      sosAlerts: [],
      isTransmitting: false,
      isPttLocked: false,
      error: null,
      audioEngine: null,
      userRole: '',
      token: '',
    });
  }, []);

  // ─── Auto-login on mount ───
  useEffect(() => {
    const token = localStorage.getItem('hemaya_token');
    const role = localStorage.getItem('hemaya_role') || '';
    if (token) {
      setState(s => ({ ...s, token, userRole: role }));
      connect(token);
    }
    return () => {
      socketRef.current?.disconnect();
      audioEngineRef.current?.destroy();
    };
  }, [connect]);

  // ─── Channel management helpers ───
  const refreshChannels = useCallback(() => {
    const t = localStorage.getItem('hemaya_token') || '';
    fetch(`https://secure.hemaya.cloud/api/walkie/channels`, {
      headers: { Authorization: `Bearer ${t}` },
    }).then(r => r.json()).then(channels => {
      if (Array.isArray(channels)) setState(s => ({ ...s, channels }));
    }).catch(() => {});
  }, []);

  const createChannel = useCallback(async (name: string, type: string, maxUsers: number) => {
    const t = localStorage.getItem('hemaya_token') || '';
    const res = await fetch(`https://secure.hemaya.cloud/api/walkie/channels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
      body: JSON.stringify({ name, type, maxUsers }),
    });
    if (!res.ok) throw new Error((await res.json()).message || 'Create failed');
    refreshChannels();
  }, [refreshChannels]);

  const deleteChannel = useCallback(async (channelId: string) => {
    const t = localStorage.getItem('hemaya_token') || '';
    const res = await fetch(`https://secure.hemaya.cloud/api/walkie/channels/${channelId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) throw new Error((await res.json()).message || 'Delete failed');
    refreshChannels();
  }, [refreshChannels]);

  const addMemberToChannel = useCallback(async (channelId: string, employeeId: string) => {
    const t = localStorage.getItem('hemaya_token') || '';
    const res = await fetch(`https://secure.hemaya.cloud/api/walkie/channels/${channelId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
      body: JSON.stringify({ employeeId }),
    });
    if (!res.ok) throw new Error((await res.json()).message || 'Join failed');
  }, []);

  const removeMemberFromChannel = useCallback(async (channelId: string, userId: string) => {
    const t = localStorage.getItem('hemaya_token') || '';
    const res = await fetch(`https://secure.hemaya.cloud/api/walkie/channels/${channelId}/members/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) throw new Error((await res.json()).message || 'Leave failed');
  }, []);

  // ─── Keyboard shortcuts handled in components, but expose state ───
  const value: WalkieContextValue = {
    ...state,
    audioEngine: audioEngineRef.current,
    pttStart,
    pttStop,
    switchChannel,
    toggleMemberMute,
    resolveSOS,
    login,
    logout,
    userRole: state.userRole,
    token: state.token,
    createChannel,
    deleteChannel,
    addMemberToChannel,
    removeMemberFromChannel,
    refreshChannels,
  };

  return <WalkieContext.Provider value={value}>{children}</WalkieContext.Provider>;
}
